import { useEffect, useMemo, useState } from 'react';
import { BarChart3, Download, Pencil, PlayCircle, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import type { SupportedLanguage } from '../i18n/translations';
import { useApiClient } from '../lib/apiClient';

type SavedReport = {
  report_id: string;
  owner_user_id: string;
  name: string;
  query_json: Record<string, unknown>;
  visualization_json: Record<string, unknown>;
  version: number;
  updated_at: string;
};

type ScheduledReport = {
  schedule_id: string;
  report_id: string;
  cron_expr: string | null;
  status: string;
  last_run_at: string | null;
  next_run_at: string;
};

type DatasetRegistryItem = {
  dataset: string;
  version: number;
  allowedFields: string[];
  allowedFilters: string[];
  status: string;
};

type TreatmentPlanOverviewSummary = {
  totalPlans: number;
  activePlans: number;
  completedPlans: number;
  overdueReviewCount: number;
  dueSoonReviewCount: number;
  patientsWithActivePlans: number;
  averageProgressPercent: number;
  averageAdherencePercent: number | null;
  linkedVisitCount: number;
  attendedVisitCount: number;
};

type TreatmentPlanOverviewRow = {
  id: string;
  patient: {
    id: string;
    name: string;
  };
  ownerName: string | null;
  title: string;
  diagnosis: string | null;
  diagnosisCodes: string[];
  templateCode: string | null;
  category: string | null;
  status: string;
  priority: string | null;
  nextReviewAt: string | null;
  updatedAt: string;
  suggestedVisitCount: number | null;
  visitProgress: {
    linkedAppointmentCount: number;
    attendedVisitCount: number;
    upcomingVisitCount: number;
    remainingSuggestedVisitCount: number | null;
  };
  linkedNotesCount: number;
  progressPercent: number;
  adherencePercent: number | null;
};

type TreatmentPlanOverviewResponse = {
  summary: TreatmentPlanOverviewSummary;
  data: TreatmentPlanOverviewRow[];
};

type BuilderFilterOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'in' | 'contains';
type BuilderFilter = {
  id: number;
  field: string;
  op: BuilderFilterOp;
  value: string;
  valueTo: string;
};

type PhraseTranslator = (phrase: string) => string;

const datasetDateFieldMap: Record<string, string> = {
  patients: 'created_at',
  encounters: 'encounter_date',
  appointments: 'start_time',
  labs: 'result_date',
  medications: 'start_date',
  billing: 'billed_date',
};

const filterOps: BuilderFilterOp[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'contains'];

const localeByLanguage: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const treatmentPlanStatusOptions = ['', 'DRAFT', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'DISCONTINUED'] as const;

const reportsEsOverrides: Record<string, string> = {
  'Reports Workspace': 'Espacio de reportes',
  'Custom report definitions, execution, and result previews in one workspace.':
    'Definiciones de reportes personalizados, ejecucion y vista previa de resultados en un solo espacio.',
  'Create Report': 'Crear reporte',
  'Cancel Report': 'Cancelar reporte',
  'Recent Reports': 'Reportes recientes',
  'Showing saved custom reports with execution controls.': 'Mostrando reportes personalizados guardados con controles de ejecucion.',
  'Search reports...': 'Buscar reportes...',
  Name: 'Nombre',
  Dataset: 'Conjunto de datos',
  Version: 'Version',
  Schedules: 'Programaciones',
  'Next Run': 'Proxima ejecucion',
  'Last Updated': 'Ultima actualizacion',
  Actions: 'Acciones',
  Edit: 'Editar',
  Delete: 'Eliminar',
  Run: 'Ejecutar',
  'No reports found': 'No se encontraron reportes',
  'Report Builder': 'Constructor de reportes',
  'Define the custom report and save it for later execution.': 'Define el reporte personalizado y guardalo para ejecutarlo despues.',
  Close: 'Cerrar',
  'Report Name': 'Nombre del reporte',
  'Date From': 'Fecha desde',
  'Date To': 'Fecha hasta',
  Filters: 'Filtros',
  Value: 'Valor',
  'To value': 'Hasta valor',
  Optional: 'Opcional',
  'Add filter': 'Agregar filtro',
  Fields: 'Campos',
  'Group by': 'Agrupar por',
  Aggregation: 'Agregacion',
  'Aggregation Field': 'Campo de agregacion',
  Alias: 'Alias',
  Percentile: 'Percentil',
  Visualization: 'Visualizacion',
  'Row limit': 'Limite de filas',
  Cancel: 'Cancelar',
  'Save Report': 'Guardar reporte',
  'Update Report': 'Actualizar reporte',
  'Delete this report?': 'Eliminar este reporte?',
  'Failed to load reports': 'No se pudieron cargar los reportes',
  'Failed to save report': 'No se pudo guardar el reporte',
  'Failed to delete report': 'No se pudo eliminar el reporte',
  'Failed to run report': 'No se pudo ejecutar el reporte',
  'Report deleted': 'Reporte eliminado',
  'Report saved': 'Reporte guardado',
  'Report updated': 'Reporte actualizado',
  'Report Results': 'Resultados del reporte',
  'Run a report from the table to generate preview results.': 'Ejecuta un reporte desde la tabla para generar resultados de vista previa.',
  Status: 'Estado',
  Rows: 'Filas',
  'No preview rows returned.': 'No se devolvieron filas de vista previa.',
  Scheduled: 'Programado',
  Manual: 'Manual',
  'Create and save a new custom report.': 'Crea y guarda un nuevo reporte personalizado.',
  'Editing saved report definition.': 'Editando la definicion del reporte guardado.',
  count: 'conteo',
  count_distinct: 'conteo_distinto',
  avg: 'promedio',
  sum: 'suma',
  percentile: 'percentil',
  table: 'tabla',
  line: 'linea',
  bar: 'barras',
  area: 'area',
  pie: 'circular',
  cohort: 'cohorte',
  SUCCEEDED: 'EXITOSA',
  QUEUED: 'EN_COLA',
  RUNNING: 'EN_EJECUCION',
  FAILED: 'FALLIDA',
};

const reportsItOverrides: Record<string, string> = {
  'Reports Workspace': 'Workspace reportistica',
  Reports: 'Report',
  'Custom report definitions, execution, and result previews in one workspace.':
    'Definizioni report personalizzati, esecuzione e anteprima risultati in un unico workspace.',
  'Create Report': 'Crea report',
  'Cancel Report': 'Annulla report',
  'Recent Reports': 'Report recenti',
  'Showing saved custom reports with execution controls.': 'Mostra report personalizzati salvati con controlli di esecuzione.',
  'Search reports...': 'Cerca report...',
  Name: 'Nome',
  Dataset: 'Dataset',
  Version: 'Versione',
  Schedules: 'Pianificazioni',
  'Next Run': 'Prossima esecuzione',
  'Last Updated': 'Ultimo aggiornamento',
  Actions: 'Azioni',
  Edit: 'Modifica',
  Delete: 'Elimina',
  Run: 'Esegui',
  'No reports found': 'Nessun report trovato',
  'Report Builder': 'Builder report',
  'Define the custom report and save it for later execution.': 'Definisci il report personalizzato e salvalo per eseguirlo dopo.',
  Close: 'Chiudi',
  'Report Name': 'Nome report',
  'Date From': 'Data da',
  'Date To': 'Data a',
  Filters: 'Filtri',
  Value: 'Valore',
  'To value': 'A valore',
  Optional: 'Opzionale',
  'Add filter': 'Aggiungi filtro',
  Fields: 'Campi',
  'Group by': 'Raggruppa per',
  Aggregation: 'Aggregazione',
  'Aggregation Field': 'Campo aggregazione',
  Alias: 'Alias',
  Percentile: 'Percentile',
  Visualization: 'Visualizzazione',
  'Row limit': 'Limite righe',
  Cancel: 'Annulla',
  'Save Report': 'Salva report',
  'Update Report': 'Aggiorna report',
  'Delete this report?': 'Eliminare questo report?',
  'Failed to load reports': 'Impossibile caricare i report',
  'Failed to save report': 'Impossibile salvare il report',
  'Failed to delete report': 'Impossibile eliminare il report',
  'Failed to run report': 'Impossibile eseguire il report',
  'Report deleted': 'Report eliminato',
  'Report saved': 'Report salvato',
  'Report updated': 'Report aggiornato',
  'Report Results': 'Risultati report',
  'Run a report from the table to generate preview results.': 'Esegui un report dalla tabella per generare l anteprima risultati.',
  Status: 'Stato',
  Rows: 'Righe',
  'No preview rows returned.': 'Nessuna riga restituita.',
  Scheduled: 'Pianificato',
  Manual: 'Manuale',
  'Create and save a new custom report.': 'Crea e salva un nuovo report personalizzato.',
  'Editing saved report definition.': 'Modifica della definizione del report salvato.',
  count: 'conteggio',
  count_distinct: 'conteggio_distinto',
  avg: 'media',
  sum: 'somma',
  percentile: 'percentile',
  table: 'tabella',
  line: 'linea',
  bar: 'barre',
  area: 'area',
  pie: 'torta',
  cohort: 'coorte',
  SUCCEEDED: 'RIUSCITA',
  QUEUED: 'IN_CODA',
  RUNNING: 'IN_ESECUZIONE',
  FAILED: 'FALLITA',
};

function getLocale(language: SupportedLanguage) {
  return localeByLanguage[language] ?? 'en-US';
}

function createReportsTranslator(language: SupportedLanguage, tr: PhraseTranslator): PhraseTranslator {
  if (language === 'en') return tr;

  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;

    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) {
      return phrase.replace(normalized, globalTranslation);
    }

    const overrides = language === 'es' ? reportsEsOverrides : language === 'it' ? reportsItOverrides : null;
    if (overrides?.[normalized]) {
      return phrase.replace(normalized, overrides[normalized]);
    }

    return phrase;
  };
}

function parseFilterValue(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  return trimmed;
}

function safeArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function humanizeDatasetName(dataset: string) {
  return dataset
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

export default function Reports() {
  const { tr: baseTr, language } = useI18n();
  const tr = useMemo(() => createReportsTranslator(language, baseTr), [language, baseTr]);
  const locale = getLocale(language);
  const { fetchApi, fetchJson } = useApiClient();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [schedules, setSchedules] = useState<ScheduledReport[]>([]);
  const [datasets, setDatasets] = useState<DatasetRegistryItem[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<SavedReport | null>(null);
  const [suppressDatasetDefaults, setSuppressDatasetDefaults] = useState(false);

  const [reportName, setReportName] = useState(tr('Custom Report'));
  const [builderDataset, setBuilderDataset] = useState('patients');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [groupByFields, setGroupByFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<BuilderFilter[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [aggregationType, setAggregationType] = useState<'count' | 'count_distinct' | 'avg' | 'sum' | 'percentile'>('count');
  const [aggregationField, setAggregationField] = useState('');
  const [aggregationAlias, setAggregationAlias] = useState('metric_value');
  const [percentileValue, setPercentileValue] = useState(90);
  const [visualization, setVisualization] = useState<'table' | 'line' | 'bar' | 'area' | 'pie' | 'cohort'>('bar');
  const [rowLimit, setRowLimit] = useState(100);

  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const [lastRunStatus, setLastRunStatus] = useState<string | null>(null);
  const [lastRunReportName, setLastRunReportName] = useState<string>('');
  const [previewRows, setPreviewRows] = useState<Array<Record<string, unknown>>>([]);
  const [treatmentPlanOverview, setTreatmentPlanOverview] = useState<TreatmentPlanOverviewResponse | null>(null);
  const [overviewStatusFilter, setOverviewStatusFilter] = useState('');
  const [overviewOwnerQuery, setOverviewOwnerQuery] = useState('');
  const [overviewDiagnosisCode, setOverviewDiagnosisCode] = useState('');
  const [overviewTemplateCode, setOverviewTemplateCode] = useState('');
  const [overviewCategory, setOverviewCategory] = useState('');
  const [exportingOverview, setExportingOverview] = useState(false);

  const currentDataset = useMemo(
    () => datasets.find((dataset) => dataset.dataset === builderDataset) ?? null,
    [builderDataset, datasets],
  );

  const schedulesByReportId = useMemo(() => {
    const map = new Map<string, ScheduledReport[]>();
    for (const schedule of schedules) {
      const existing = map.get(schedule.report_id) ?? [];
      existing.push(schedule);
      map.set(schedule.report_id, existing);
    }
    return map;
  }, [schedules]);

  const reportRows = useMemo(() => {
    const rows = savedReports.map((report) => {
      const reportSchedules = schedulesByReportId.get(report.report_id) ?? [];
      const active = reportSchedules.some((schedule) => schedule.status === 'ACTIVE');
      const nextRun =
        reportSchedules
          .filter((schedule) => schedule.next_run_at)
          .map((schedule) => new Date(schedule.next_run_at))
          .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
      return {
        ...report,
        dataset: typeof report.query_json?.dataset === 'string' ? String(report.query_json.dataset) : 'patients',
        schedules: reportSchedules.length,
        status: active ? 'Scheduled' : 'Manual',
        nextRun: nextRun ? nextRun.toLocaleString(locale) : 'N/A',
      };
    });

    const q = search.trim().toLowerCase();
    return rows
      .filter((row) => {
        if (!q) return true;
        return [row.name, row.dataset, row.status].join(' ').toLowerCase().includes(q);
      })
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [locale, savedReports, schedulesByReportId, search]);

  const previewColumns = Object.keys(previewRows[0] ?? {});

  const toggleValue = (values: string[], value: string, setter: (next: string[]) => void) => {
    if (values.includes(value)) setter(values.filter((entry) => entry !== value));
    else setter([...values, value]);
  };

  const buildTreatmentPlanOverviewQuery = (options?: { includeLimit?: boolean; format?: 'csv' }) => {
    const params = new URLSearchParams();
    params.set('reviewWindowDays', '7');
    if (options?.includeLimit !== false) params.set('limit', '8');
    if (overviewStatusFilter) params.set('status', overviewStatusFilter);
    if (overviewOwnerQuery.trim()) params.set('ownerQuery', overviewOwnerQuery.trim());
    if (overviewDiagnosisCode.trim()) params.set('diagnosisCode', overviewDiagnosisCode.trim());
    if (overviewTemplateCode.trim()) params.set('templateCode', overviewTemplateCode.trim());
    if (overviewCategory.trim()) params.set('category', overviewCategory.trim());
    if (options?.format) params.set('format', options.format);
    return params.toString();
  };

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const treatmentPlanQuery = buildTreatmentPlanOverviewQuery();
      const [savedResponse, schedulesResponse, datasetsResponse, treatmentPlanOverviewResponse] = await Promise.all([
        fetchJson<{ data: SavedReport[] }>('/analytics/reports/saved'),
        fetchJson<{ data: ScheduledReport[] }>('/analytics/reports/schedules'),
        fetchJson<{ data: DatasetRegistryItem[] }>('/analytics/datasets'),
        fetchJson<TreatmentPlanOverviewResponse>(`/treatment-plans/reports/overview?${treatmentPlanQuery}`),
      ]);
      const nextDatasets = datasetsResponse.data ?? [];
      setSavedReports(savedResponse.data ?? []);
      setSchedules(schedulesResponse.data ?? []);
      setDatasets(nextDatasets);
      setTreatmentPlanOverview(treatmentPlanOverviewResponse ?? null);
      if (nextDatasets.length && !nextDatasets.some((dataset) => dataset.dataset === builderDataset)) {
        setBuilderDataset(nextDatasets[0].dataset);
      }
    } catch (err: any) {
      setError(err?.message ?? tr('Failed to load reports'));
    } finally {
      setLoading(false);
    }
  };

  async function exportTreatmentPlanOverview() {
    setExportingOverview(true);
    setError(null);
    try {
      const response = await fetchApi(`/treatment-plans/reports/overview/export?${buildTreatmentPlanOverviewQuery({ includeLimit: false, format: 'csv' })}`);
      if (!response.ok) {
        const bodyText = await response.text();
        throw new Error(bodyText || `Request failed ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `treatment-plan-overview-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      setError(err?.message ?? tr('Failed to export treatment plan overview.'));
    } finally {
      setExportingOverview(false);
    }
  }

  useEffect(() => {
    void loadReports();
  }, []);

  useEffect(() => {
    if (!dialogOpen || !currentDataset) return;
    if (suppressDatasetDefaults) {
      setSuppressDatasetDefaults(false);
      return;
    }

    const preferredFields = ['provider_name', 'status', 'patient_name', 'patient_id'];
    const defaults = preferredFields.filter((field) => currentDataset.allowedFields.includes(field));
    const fallbackFields = currentDataset.allowedFields.slice(0, 2);
    const nextSelectedFields = (defaults.length ? defaults : fallbackFields).slice(0, 3);
    const defaultFilterField = currentDataset.allowedFilters.includes('status')
      ? 'status'
      : currentDataset.allowedFilters[0] ?? '';

    setSelectedFields(nextSelectedFields);
    setGroupByFields(nextSelectedFields.slice(0, 1));
    setAggregationField(currentDataset.allowedFields.includes('patient_id') ? 'patient_id' : currentDataset.allowedFields[0] ?? '');
    setAggregationAlias('metric_value');
    setAggregationType('count');
    setPercentileValue(90);
    setVisualization('bar');
    setRowLimit(100);
    setDateFrom('');
    setDateTo('');
    setFilters(
      defaultFilterField
        ? [{ id: Date.now(), field: defaultFilterField, op: 'eq', value: '', valueTo: '' }]
        : [],
    );
  }, [currentDataset, dialogOpen, suppressDatasetDefaults]);

  function resetBuilderForCreate() {
    setEditingReport(null);
    setReportName(tr('Custom Report'));
    setBuilderDataset(datasets[0]?.dataset ?? 'patients');
    setSuppressDatasetDefaults(false);
    setDialogOpen(true);
  }

  function hydrateBuilderFromReport(report: SavedReport) {
    const query = report.query_json ?? {};
    const dataset = typeof query.dataset === 'string' ? query.dataset : datasets[0]?.dataset ?? 'patients';
    const fields = safeArray(query.fields).filter((field): field is string => typeof field === 'string');
    const groupBy = safeArray(query.groupBy).filter((field): field is string => typeof field === 'string');
    const aggregations = safeArray(query.aggregations);
    const aggregation = (aggregations[0] ?? {}) as Record<string, unknown>;
    const dateField = datasetDateFieldMap[dataset];
    const rawFilters = safeArray(query.filters);
    const nextFilters: BuilderFilter[] = [];
    let nextDateFrom = '';
    let nextDateTo = '';

    rawFilters.forEach((filter, index) => {
      if (!filter || typeof filter !== 'object') return;
      const typedFilter = filter as Record<string, unknown>;
      const field = typeof typedFilter.field === 'string' ? typedFilter.field : '';
      const op = typeof typedFilter.op === 'string' ? (typedFilter.op as BuilderFilterOp) : 'eq';
      const value = typedFilter.value;

      if (field === dateField && op === 'between' && Array.isArray(value)) {
        nextDateFrom = String(value[0] ?? '');
        nextDateTo = String(value[1] ?? '');
        return;
      }

      nextFilters.push({
        id: Date.now() + index,
        field,
        op,
        value: Array.isArray(value) ? String(value.join(', ')) : String(value ?? ''),
        valueTo: op === 'between' && Array.isArray(value) ? String(value[1] ?? '') : '',
      });
    });

    setEditingReport(report);
    setReportName(report.name);
    setSuppressDatasetDefaults(true);
    setBuilderDataset(dataset);
    setSelectedFields(fields);
    setGroupByFields(groupBy);
    setFilters(nextFilters);
    setDateFrom(nextDateFrom);
    setDateTo(nextDateTo);
    setAggregationType(
      aggregation.type === 'count_distinct' ||
        aggregation.type === 'avg' ||
        aggregation.type === 'sum' ||
        aggregation.type === 'percentile'
        ? aggregation.type
        : 'count',
    );
    setAggregationField(typeof aggregation.field === 'string' ? aggregation.field : '');
    setAggregationAlias(typeof aggregation.as === 'string' ? aggregation.as : 'metric_value');
    setPercentileValue(typeof aggregation.percentile === 'number' ? aggregation.percentile : 90);
    setVisualization(
      query.visualization === 'table' ||
        query.visualization === 'line' ||
        query.visualization === 'bar' ||
        query.visualization === 'area' ||
        query.visualization === 'pie' ||
        query.visualization === 'cohort'
        ? query.visualization
        : report.visualization_json?.type === 'table' ||
            report.visualization_json?.type === 'line' ||
            report.visualization_json?.type === 'bar' ||
            report.visualization_json?.type === 'area' ||
            report.visualization_json?.type === 'pie' ||
            report.visualization_json?.type === 'cohort'
          ? (report.visualization_json.type as 'table' | 'line' | 'bar' | 'area' | 'pie' | 'cohort')
          : 'bar',
    );
    setRowLimit(typeof query.limit === 'number' ? query.limit : 100);
    setDialogOpen(true);
  }

  function buildStructuredQuery() {
    const queryFilters: Array<{ field: string; op: BuilderFilterOp; value: unknown }> = [];
    const dateField = datasetDateFieldMap[builderDataset];
    if (dateFrom && dateTo && dateField && currentDataset?.allowedFilters.includes(dateField)) {
      queryFilters.push({ field: dateField, op: 'between', value: [dateFrom, dateTo] });
    }

    for (const filter of filters) {
      if (!filter.field) continue;
      if (filter.op === 'between') {
        if (!filter.value || !filter.valueTo) continue;
        queryFilters.push({
          field: filter.field,
          op: filter.op,
          value: [parseFilterValue(filter.value), parseFilterValue(filter.valueTo)],
        });
        continue;
      }
      if (filter.op === 'in') {
        if (!filter.value) continue;
        queryFilters.push({
          field: filter.field,
          op: filter.op,
          value: filter.value
            .split(',')
            .map((entry) => parseFilterValue(entry))
            .filter((entry) => entry !== ''),
        });
        continue;
      }
      if (!filter.value) continue;
      queryFilters.push({
        field: filter.field,
        op: filter.op,
        value: parseFilterValue(filter.value),
      });
    }

    const safeSelectedFields = selectedFields.length ? selectedFields : currentDataset?.allowedFields.slice(0, 1) ?? [];
    const safeGroupBy = groupByFields.filter((field) => safeSelectedFields.includes(field));
    const alias = aggregationAlias.trim() || 'metric_value';
    const aggregations: Array<Record<string, unknown>> = [];

    if (aggregationType === 'count') {
      aggregations.push({ type: 'count', as: alias });
    } else if (aggregationType === 'percentile') {
      aggregations.push({ type: 'percentile', field: aggregationField, as: alias, percentile: percentileValue });
    } else {
      aggregations.push({ type: aggregationType, field: aggregationField, as: alias });
    }

    const fieldsForQuery = aggregations.length ? safeGroupBy : safeSelectedFields;

    return {
      dataset: builderDataset,
      fields: fieldsForQuery,
      filters: queryFilters,
      groupBy: safeGroupBy,
      aggregations,
      sort: [{ field: alias, direction: 'desc' }],
      limit: Math.max(1, Math.min(10000, rowLimit)),
      offset: 0,
      visualization,
    };
  }

  const addFilter = () => {
    if (!currentDataset || !currentDataset.allowedFilters.length) return;
    setFilters((prev) => [
      ...prev,
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        field: currentDataset.allowedFilters[0],
        op: 'eq',
        value: '',
        valueTo: '',
      },
    ]);
  };

  const updateFilter = (id: number, patch: Partial<BuilderFilter>) => {
    setFilters((prev) => prev.map((filter) => (filter.id === id ? { ...filter, ...patch } : filter)));
  };

  const removeFilter = (id: number) => {
    setFilters((prev) => prev.filter((filter) => filter.id !== id));
  };

  async function saveReport() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const query = buildStructuredQuery();
      await fetchJson('/analytics/reports/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      const body = {
        name: reportName.trim() || tr('Custom Report'),
        query,
        visualization: { type: visualization },
      };

      if (editingReport) {
        await fetchJson(`/analytics/reports/saved/${editingReport.report_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        setMessage(tr('Report updated'));
      } else {
        await fetchJson('/analytics/reports/saved', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        setMessage(tr('Report saved'));
      }

      setDialogOpen(false);
      await loadReports();
    } catch (err: any) {
      setError(err?.message ?? tr('Failed to save report'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteReport(reportId: string) {
    if (!window.confirm(tr('Delete this report?'))) return;
    setError(null);
    setMessage(null);
    try {
      await fetchJson(`/analytics/reports/saved/${reportId}`, { method: 'DELETE' });
      setSavedReports((prev) => prev.filter((report) => report.report_id !== reportId));
      setMessage(tr('Report deleted'));
      if (editingReport?.report_id === reportId) {
        setDialogOpen(false);
        setEditingReport(null);
      }
    } catch (err: any) {
      setError(err?.message ?? tr('Failed to delete report'));
    }
  }

  async function runSavedReport(report: SavedReport) {
    setRunningId(report.report_id);
    setError(null);
    setMessage(null);
    try {
      const response = await fetchJson<{
        data: {
          runId: string;
          status: 'SUCCEEDED' | 'QUEUED' | 'RUNNING' | 'FAILED';
          rows?: Array<Record<string, unknown>>;
        };
      }>('/analytics/reports/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: report.query_json, sourceType: 'saved-report', reportId: report.report_id }),
      });

      setLastRunId(response.data.runId);
      setLastRunStatus(response.data.status);
      setLastRunReportName(report.name);

      if (response.data.status === 'SUCCEEDED') {
        setPreviewRows(response.data.rows ?? []);
      } else {
        const results = await fetchJson<{ data: Array<Record<string, unknown>> }>(
          `/analytics/reports/runs/${response.data.runId}/results?limit=250&offset=0`,
        );
        setPreviewRows(results.data ?? []);
      }
    } catch (err: any) {
      setError(err?.message ?? tr('Failed to run report'));
    } finally {
      setRunningId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tr('Reports Workspace')}</p>
            <h1 className="mt-1 text-2xl font-semibold text-ink">{tr('Reports')}</h1>
            <p className="max-w-3xl text-sm text-slate-600">
              {tr('Custom report definitions, execution, and result previews in one workspace.')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadReports}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw size={16} />
              {tr('Refresh')}
            </button>
            <button
              type="button"
              onClick={resetBuilderForCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300"
            >
              <Plus size={16} />
              {tr('Create Report')}
            </button>
          </div>
        </div>
      </div>

      {message ? (
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</div>
      ) : null}
      {error ? (
        <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
      ) : null}

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{tr('Treatment Plan Snapshot')}</h2>
              <p className="text-sm text-slate-500">{tr('Overdue reviews, active-plan load, and recent plan movement across the clinic.')}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Phase 4 overview')}</div>
              <button
                type="button"
                onClick={() => void exportTreatmentPlanOverview()}
                disabled={exportingOverview}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <Download size={14} />
                {exportingOverview ? tr('Loading...') : tr('Export CSV')}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 border-b border-slate-200 px-4 py-4 md:grid-cols-2 xl:grid-cols-6">
          <label className="text-sm text-slate-600">
            {tr('Status')}
            <select value={overviewStatusFilter} onChange={(event) => setOverviewStatusFilter(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white">
              {treatmentPlanStatusOptions.map((status) => (
                <option key={status || 'all-statuses'} value={status}>
                  {status ? tr(status) : tr('All statuses')}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-slate-600">
            {tr('Owner')}
            <input value={overviewOwnerQuery} onChange={(event) => setOverviewOwnerQuery(event.target.value)} placeholder={tr('Clinician name')} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white" />
          </label>
          <label className="text-sm text-slate-600">
            {tr('Diagnosis Code')}
            <input value={overviewDiagnosisCode} onChange={(event) => setOverviewDiagnosisCode(event.target.value)} placeholder={tr('Primary code')} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white" />
          </label>
          <label className="text-sm text-slate-600">
            {tr('Template Code')}
            <input value={overviewTemplateCode} onChange={(event) => setOverviewTemplateCode(event.target.value)} placeholder={tr('Template code')} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white" />
          </label>
          <label className="text-sm text-slate-600">
            {tr('Category')}
            <input value={overviewCategory} onChange={(event) => setOverviewCategory(event.target.value)} placeholder={tr('Category')} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white" />
          </label>
          <div className="flex items-end gap-2">
            <button type="button" onClick={loadReports} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-lime-400 px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-lime-300">
              <RefreshCw size={14} />
              {tr('Apply Filters')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-3 xl:grid-cols-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Active Plans')}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{(treatmentPlanOverview?.summary.activePlans ?? 0).toLocaleString(locale)}</div>
            <div className="mt-1 text-xs text-slate-500">{(treatmentPlanOverview?.summary.patientsWithActivePlans ?? 0).toLocaleString(locale)} {tr('patients')}</div>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-rose-600">{tr('Overdue Reviews')}</div>
            <div className="mt-2 text-2xl font-semibold text-rose-700">{(treatmentPlanOverview?.summary.overdueReviewCount ?? 0).toLocaleString(locale)}</div>
            <div className="mt-1 text-xs text-rose-600">{tr('Needs action now')}</div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-amber-700">{tr('Due Soon')}</div>
            <div className="mt-2 text-2xl font-semibold text-amber-800">{(treatmentPlanOverview?.summary.dueSoonReviewCount ?? 0).toLocaleString(locale)}</div>
            <div className="mt-1 text-xs text-amber-700">{tr('Within 7 days')}</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-emerald-700">{tr('Average Progress')}</div>
            <div className="mt-2 text-2xl font-semibold text-emerald-800">{(treatmentPlanOverview?.summary.averageProgressPercent ?? 0).toLocaleString(locale)}%</div>
            <div className="mt-1 text-xs text-emerald-700">{tr('Across current report set')}</div>
          </div>
          <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-sky-700">{tr('Attended Visits')}</div>
            <div className="mt-2 text-2xl font-semibold text-sky-800">{(treatmentPlanOverview?.summary.attendedVisitCount ?? 0).toLocaleString(locale)}</div>
            <div className="mt-1 text-xs text-sky-700">{(treatmentPlanOverview?.summary.linkedVisitCount ?? 0).toLocaleString(locale)} {tr('linked overall')}</div>
          </div>
        </div>

        <div className="overflow-x-auto border-t border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">{tr('Patient')}</th>
                <th className="px-4 py-3 font-medium">{tr('Plan')}</th>
                <th className="px-4 py-3 font-medium">{tr('Owner')}</th>
                <th className="px-4 py-3 font-medium">{tr('Review')}</th>
                <th className="px-4 py-3 font-medium">{tr('Visits')}</th>
                <th className="px-4 py-3 font-medium">{tr('Progress')}</th>
                <th className="px-4 py-3 font-medium">{tr('Updated')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(treatmentPlanOverview?.data ?? []).map((plan) => {
                const reviewTone =
                  plan.nextReviewAt && new Date(plan.nextReviewAt).getTime() < Date.now()
                    ? 'text-rose-700'
                    : plan.nextReviewAt
                      ? 'text-amber-700'
                      : 'text-slate-500';
                return (
                  <tr key={plan.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-900">{plan.patient.name}</div>
                      <div className="text-xs text-slate-500">{plan.diagnosisCodes[0] || plan.diagnosis || tr('No diagnosis')}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-800">{plan.title}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700">{tr(plan.status)}</span>
                        {plan.category ? <span className="text-[11px] text-slate-500">{plan.category}</span> : null}
                        {plan.templateCode ? <span className="text-[11px] text-slate-500">{plan.templateCode}</span> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-600">{plan.ownerName || '—'}</td>
                    <td className={`px-4 py-3 align-top ${reviewTone}`}>{plan.nextReviewAt ? new Date(plan.nextReviewAt).toLocaleDateString(locale) : '—'}</td>
                    <td className="px-4 py-3 align-top text-slate-600">
                      {plan.visitProgress.attendedVisitCount}
                      {plan.suggestedVisitCount ? ` / ${plan.suggestedVisitCount}` : ''}
                      <div className="text-xs text-slate-500">{plan.visitProgress.upcomingVisitCount} {tr('upcoming')}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-600">
                      <div>{plan.progressPercent}%</div>
                      <div className="text-xs text-slate-500">{plan.linkedNotesCount} {tr('notes linked')}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-600">{new Date(plan.updatedAt).toLocaleDateString(locale)}</td>
                  </tr>
                );
              })}
              {!(treatmentPlanOverview?.data ?? []).length ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-sm text-slate-500">
                    {loading ? tr('Loading...') : tr('No treatment plans available for reporting.')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{tr('Recent Reports')}</h2>
            <p className="text-sm text-slate-500">{tr('Showing saved custom reports with execution controls.')}</p>
          </div>
          <div className="relative w-full max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={tr('Search reports...')}
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">{tr('Name')}</th>
                <th className="px-4 py-3 font-medium">{tr('Dataset')}</th>
                <th className="px-4 py-3 font-medium">{tr('Version')}</th>
                <th className="px-4 py-3 font-medium">{tr('Schedules')}</th>
                <th className="px-4 py-3 font-medium">{tr('Status')}</th>
                <th className="px-4 py-3 font-medium">{tr('Next Run')}</th>
                <th className="px-4 py-3 font-medium">{tr('Last Updated')}</th>
                <th className="px-4 py-3 font-medium text-right">{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reportRows.map((row) => (
                <tr key={row.report_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-slate-800">{row.name}</div>
                  </td>
                  <td className="px-4 py-3 align-top text-slate-600">{tr(row.dataset)}</td>
                  <td className="px-4 py-3 align-top text-slate-600">{row.version.toLocaleString(locale)}</td>
                  <td className="px-4 py-3 align-top text-slate-600">{row.schedules.toLocaleString(locale)}</td>
                  <td className="px-4 py-3 align-top">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${row.status === 'Scheduled' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                      {tr(row.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-slate-600">{row.nextRun === 'N/A' ? row.nextRun : row.nextRun}</td>
                  <td className="px-4 py-3 align-top text-slate-600">{new Date(row.updated_at).toLocaleString(locale)}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => hydrateBuilderFromReport(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <Pencil size={12} />
                        {tr('Edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteReport(row.report_id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 size={12} />
                        {tr('Delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => runSavedReport(row)}
                        disabled={runningId === row.report_id}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        <PlayCircle size={12} />
                        {tr('Run')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!reportRows.length ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-sm text-slate-500">
                    {loading ? tr('Loading...') : tr('No reports found')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{tr('Report Results')}</h2>
              <p className="text-sm text-slate-500">
                {lastRunReportName
                  ? `${lastRunReportName}${lastRunStatus ? ` • ${tr('Status')}: ${tr(lastRunStatus)}` : ''}${lastRunId ? ` • ${tr('Run')}: ${lastRunId}` : ''}`
                  : tr('Run a report from the table to generate preview results.')}
              </p>
            </div>
            {lastRunId ? (
              <div className="inline-flex items-center gap-2 rounded-full bg-lime-50 px-3 py-1.5 text-sm font-medium text-lime-800">
                <BarChart3 size={14} />
                {previewRows.length.toLocaleString(locale)} {tr('Rows')}
              </div>
            ) : null}
          </div>
        </div>

        <div className="overflow-x-auto p-4">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                {previewColumns.map((column) => (
                  <th key={`column-${column}`} className="px-4 py-3 font-medium">{column}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {previewRows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {previewColumns.map((column) => (
                    <td key={`cell-${rowIndex}-${column}`} className="px-4 py-3 text-slate-700">
                      {String(row[column] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
              {!previewRows.length ? (
                <tr>
                  <td colSpan={Math.max(1, previewColumns.length)} className="px-4 py-6 text-sm text-slate-500">
                    {tr('No preview rows returned.')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {dialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => !saving && setDialogOpen(false)}>
          <div
            className="mt-10 w-full max-w-5xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tr('Report Builder')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{editingReport ? tr('Update Report') : tr('Create Report')}</h2>
                <p className="text-sm text-slate-500">
                  {editingReport ? tr('Editing saved report definition.') : tr('Create and save a new custom report.')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <label className="text-sm text-slate-600">
                  {tr('Report Name')}
                  <input
                    value={reportName}
                    onChange={(event) => setReportName(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Dataset')}
                  <select
                    value={builderDataset}
                    onChange={(event) => setBuilderDataset(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  >
                    {(datasets.length ? datasets : [{ dataset: 'patients', version: 1, allowedFields: [], allowedFilters: [], status: 'ACTIVE' }]).map((dataset) => (
                      <option key={dataset.dataset} value={dataset.dataset}>
                        {tr(humanizeDatasetName(dataset.dataset))}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Date From')}
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(event) => setDateFrom(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Date To')}
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(event) => setDateTo(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-sm font-medium text-slate-800">{tr('Filters')}</p>
                  <div className="mt-3 space-y-2">
                    {filters.map((filter) => (
                      <div key={filter.id} className="grid grid-cols-1 gap-2 md:grid-cols-5">
                        <select
                          value={filter.field}
                          onChange={(event) => updateFilter(filter.id, { field: event.target.value })}
                          className="rounded-xl border border-slate-300 px-2 py-2 text-sm"
                        >
                          {(currentDataset?.allowedFilters ?? []).map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </select>
                        <select
                          value={filter.op}
                          onChange={(event) => updateFilter(filter.id, { op: event.target.value as BuilderFilterOp })}
                          className="rounded-xl border border-slate-300 px-2 py-2 text-sm"
                        >
                          {filterOps.map((op) => (
                            <option key={op} value={op}>
                              {op}
                            </option>
                          ))}
                        </select>
                        <input
                          value={filter.value}
                          onChange={(event) => updateFilter(filter.id, { value: event.target.value })}
                          placeholder={tr('Value')}
                          className="rounded-xl border border-slate-300 px-2 py-2 text-sm"
                        />
                        <input
                          value={filter.valueTo}
                          onChange={(event) => updateFilter(filter.id, { valueTo: event.target.value })}
                          placeholder={tr(filter.op === 'between' ? 'To value' : 'Optional')}
                          className="rounded-xl border border-slate-300 px-2 py-2 text-sm"
                          disabled={filter.op !== 'between'}
                        />
                        <button
                          type="button"
                          onClick={() => removeFilter(filter.id)}
                          className="rounded-xl border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        >
                          <Trash2 size={14} className="mx-auto" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addFilter}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Plus size={14} />
                      {tr('Add filter')}
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-sm font-medium text-slate-800">{tr('Fields')}</p>
                  <div className="mt-3 grid max-h-40 grid-cols-2 gap-2 overflow-auto">
                    {(currentDataset?.allowedFields ?? []).map((field) => (
                      <label key={`field-${field}`} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field)}
                          onChange={() => toggleValue(selectedFields, field, setSelectedFields)}
                        />
                        <span>{field}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-4 text-sm font-medium text-slate-800">{tr('Group by')}</p>
                  <div className="mt-3 grid max-h-28 grid-cols-2 gap-2 overflow-auto">
                    {selectedFields.map((field) => (
                      <label key={`group-${field}`} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={groupByFields.includes(field)}
                          onChange={() => toggleValue(groupByFields, field, setGroupByFields)}
                        />
                        <span>{field}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                <label className="text-sm text-slate-600">
                  {tr('Aggregation')}
                  <select
                    value={aggregationType}
                    onChange={(event) => setAggregationType(event.target.value as typeof aggregationType)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  >
                    <option value="count">{tr('count')}</option>
                    <option value="count_distinct">{tr('count_distinct')}</option>
                    <option value="avg">{tr('avg')}</option>
                    <option value="sum">{tr('sum')}</option>
                    <option value="percentile">{tr('percentile')}</option>
                  </select>
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Aggregation Field')}
                  <select
                    value={aggregationField}
                    onChange={(event) => setAggregationField(event.target.value)}
                    disabled={aggregationType === 'count'}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white disabled:bg-slate-50"
                  >
                    {(currentDataset?.allowedFields ?? []).map((field) => (
                      <option key={`agg-${field}`} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Alias')}
                  <input
                    value={aggregationAlias}
                    onChange={(event) => setAggregationAlias(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Percentile')}
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={percentileValue}
                    onChange={(event) => setPercentileValue(Number(event.target.value))}
                    disabled={aggregationType !== 'percentile'}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white disabled:bg-slate-50"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  {tr('Visualization')}
                  <select
                    value={visualization}
                    onChange={(event) => setVisualization(event.target.value as typeof visualization)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  >
                    <option value="table">{tr('table')}</option>
                    <option value="line">{tr('line')}</option>
                    <option value="bar">{tr('bar')}</option>
                    <option value="area">{tr('area')}</option>
                    <option value="pie">{tr('pie')}</option>
                    <option value="cohort">{tr('cohort')}</option>
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="text-sm text-slate-600">
                  {tr('Row limit')}
                  <input
                    type="number"
                    min={1}
                    max={10000}
                    value={rowLimit}
                    onChange={(event) => setRowLimit(Number(event.target.value))}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white"
                  />
                </label>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                disabled={saving}
              >
                {tr('Cancel')}
              </button>
              <button
                type="button"
                onClick={saveReport}
                className="rounded-xl bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-60"
                disabled={saving}
              >
                {saving ? tr('Loading...') : editingReport ? tr('Update Report') : tr('Save Report')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
